import { Fragment, type CSSProperties, type ReactNode } from 'react';
import { WorkspaceColumnHeader } from '@/components/workspace/WorkspaceColumnHeader';
import type { ColumnDropPosition } from '@/components/workspace/useColumnOrder';
import type { WorkspaceSortDir } from '@/components/workspace/useTableSort';
import {
  entityColumnsById,
  metricColumnsByKey,
  type WorkspaceEntityColumnDef,
} from '@/components/workspace/workspaceColumnConfig';
import {
  WORKSPACE_METRIC_COL_CLASS,
  WorkspaceMetricCell,
} from '@/components/workspace/WorkspaceMetricCell';
import { workspaceTableClass } from '@/components/workspace/WorkspaceSection';
import {
  ensureDerivedMetrics,
  WORKSPACE_METRIC_COLUMNS,
  type WorkspaceMetricColumnDef,
} from '@/utils/workspaceAdProductMetrics';
import {
  workspaceColumnStickyProps,
  workspaceStickyCheckboxFooterClass,
  workspaceStickyFooterFillClass,
} from '@/components/workspace/workspaceStickyColumns';
import {
  workspaceDraggedColumnClass,
  workspaceDropTargetClass,
} from '@/components/workspace/workspaceDragStyles';
import { WorkspaceColumnInsertIndicator } from '@/components/workspace/WorkspaceColumnInsertIndicator';

export {
  WORKSPACE_CHECKBOX_COL_SIZE_CLASS,
  WORKSPACE_FIRST_DATA_COL_LEFT_CLASS,
  workspaceColumnStickyClass,
  workspaceColumnStickyFooterClass,
  workspaceColumnStickyProps,
  workspaceStickyCheckboxCellClass,
  workspaceStickyCheckboxFooterClass,
  workspaceStickyDataCellClass,
  workspaceStickyDataFooterClass,
  workspaceStickyFooterFillClass,
} from '@/components/workspace/workspaceStickyColumns';

export {
  workspaceDraggedColumnClass,
  workspaceDropTargetClass,
} from '@/components/workspace/workspaceDragStyles';

export function WorkspaceOrderedHeaders({
  order,
  entityColumns,
  metricColumns = WORKSPACE_METRIC_COLUMNS,
  sortBy,
  sortDir,
  onSort,
  onFreeze,
  frozenColumnIds,
  pinnedFrozenColumnId,
  frozenLeftById = {},
  onDragStartColumn,
  onDragHoverColumn,
  onDropColumn,
  onDragEndColumn,
  activeDragColumnId,
  hoverColumnId,
  hoverPosition,
  hoverInsertIndex,
  beforeFirstMetric,
}: {
  order: string[];
  entityColumns: WorkspaceEntityColumnDef[];
  metricColumns?: WorkspaceMetricColumnDef[];
  sortBy: string;
  sortDir: WorkspaceSortDir;
  onSort: (id: string, dir?: WorkspaceSortDir) => void;
  onFreeze?: (columnId: string) => void;
  frozenColumnIds?: readonly string[];
  pinnedFrozenColumnId?: string | null;
  frozenLeftById?: Record<string, number>;
  onDragStartColumn: (columnId: string) => void;
  onDragHoverColumn: (
    targetId: string,
    position: ColumnDropPosition,
    clientX?: number,
    insertIndex?: number
  ) => void;
  onDropColumn: (targetId: string, position: ColumnDropPosition) => void;
  onDragEndColumn: () => void;
  activeDragColumnId: string | null;
  hoverColumnId: string | null;
  hoverPosition: ColumnDropPosition | null;
  hoverInsertIndex?: number | null;
  beforeFirstMetric?: ReactNode;
}) {
  const entityById = entityColumnsById(entityColumns);
  const metricByKey = metricColumnsByKey(metricColumns);
  let placementInserted = false;
  const pinnedId = pinnedFrozenColumnId ?? frozenColumnIds?.[0] ?? null;

  return (
    <>
      {order.map((id) => {
        const metric = metricByKey.get(id);
        const entity = entityById.get(id);
        const isDragged = activeDragColumnId === id;
        const isDropTarget = hoverColumnId === id && activeDragColumnId !== id;
        const sticky = workspaceColumnStickyProps(id, frozenColumnIds, frozenLeftById, 'header');
        const dragClass = workspaceDraggedColumnClass(isDragged);
        const dropClass = workspaceDropTargetClass(isDropTarget, hoverPosition);
        const header = entity ? (
          <WorkspaceColumnHeader
            key={id}
            label={entity.label}
            columnId={id}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={onSort}
            onFreeze={id === pinnedId ? undefined : onFreeze}
            isFrozen={id !== pinnedId && Boolean(frozenColumnIds?.includes(id))}
            onDragStartColumn={onDragStartColumn}
            onDragHoverColumn={onDragHoverColumn}
            onDropColumn={onDropColumn}
            onDragEndColumn={onDragEndColumn}
            activeDragColumnId={activeDragColumnId}
            hoverInsertIndex={hoverInsertIndex}
            isDragged={isDragged}
            isDropTarget={isDropTarget}
            dropPosition={hoverPosition}
            minWidth={entity.minWidth}
            info={entity.info}
            className={`${sticky.className} ${dragClass} ${dropClass} ${entity.className ?? ''}`.trim()}
            style={sticky.style}
          />
        ) : metric ? (
          <WorkspaceColumnHeader
            key={id}
            label={metric.label}
            columnId={id}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={onSort}
            onFreeze={id === pinnedId ? undefined : onFreeze}
            isFrozen={id !== pinnedId && Boolean(frozenColumnIds?.includes(id))}
            onDragStartColumn={onDragStartColumn}
            onDragHoverColumn={onDragHoverColumn}
            onDropColumn={onDropColumn}
            onDragEndColumn={onDragEndColumn}
            activeDragColumnId={activeDragColumnId}
            hoverInsertIndex={hoverInsertIndex}
            isDragged={isDragged}
            isDropTarget={isDropTarget}
            dropPosition={hoverPosition}
            className={`${sticky.className} ${dragClass} ${dropClass} ${WORKSPACE_METRIC_COL_CLASS}`.trim()}
            style={sticky.style}
          />
        ) : null;

        if (metric && !placementInserted && beforeFirstMetric) {
          placementInserted = true;
          return (
            <Fragment key={`wrap-${id}`}>
              {beforeFirstMetric}
              {header}
            </Fragment>
          );
        }
        return header;
      })}
      {!placementInserted && beforeFirstMetric ? beforeFirstMetric : null}
      <WorkspaceColumnInsertIndicator
        activeDragColumnId={activeDragColumnId}
        hoverColumnId={hoverColumnId}
        hoverPosition={hoverPosition}
      />
    </>
  );
}

export function WorkspaceOrderedMetricCells({
  order,
  metrics,
  previousMetrics,
  compareEnabled,
  isLoading,
  entityColumns,
  metricColumns = WORKSPACE_METRIC_COLUMNS,
  frozenColumnIds,
  frozenLeftById = {},
  activeDragColumnId,
  hoverColumnId,
  hoverPosition,
  beforeFirstMetric,
  renderEntityCell,
}: {
  order: string[];
  metrics: Record<string, number> | null;
  previousMetrics: Record<string, number> | null;
  compareEnabled: boolean;
  isLoading?: boolean;
  entityColumns?: WorkspaceEntityColumnDef[];
  metricColumns?: WorkspaceMetricColumnDef[];
  frozenColumnIds?: readonly string[];
  frozenLeftById?: Record<string, number>;
  activeDragColumnId?: string | null;
  hoverColumnId?: string | null;
  hoverPosition?: ColumnDropPosition | null;
  beforeFirstMetric?: ReactNode;
  renderEntityCell: (
    columnId: string,
    meta: { columnIndex: number; className: string; style?: CSSProperties; isDragged: boolean }
  ) => ReactNode;
}) {
  const entityById = entityColumnsById(entityColumns ?? []);
  const metricByKey = metricColumnsByKey(metricColumns);
  const current = {
    ...(metrics ?? {}),
    ...ensureDerivedMetrics(metrics),
  } as Record<string, number>;
  const previous = {
    ...(previousMetrics ?? {}),
    ...ensureDerivedMetrics(previousMetrics),
  } as Record<string, number>;
  let placementInserted = false;

  return (
    <>
      {order.map((id, index) => {
        const metric = metricByKey.get(id);
        const isDragged = activeDragColumnId === id;
        const isDropTarget = hoverColumnId === id && activeDragColumnId !== id;
        const sticky = workspaceColumnStickyProps(id, frozenColumnIds, frozenLeftById, 'body');
        const className = [
          sticky.className,
          workspaceDraggedColumnClass(isDragged),
          workspaceDropTargetClass(isDropTarget, hoverPosition ?? null),
          entityById.get(id)?.className,
        ]
          .filter(Boolean)
          .join(' ');
        if (metric) {
          const cell = (
            <WorkspaceMetricCell
              key={id}
              def={metric}
              current={current[id] ?? null}
              previous={previous[id] ?? null}
              compareEnabled={compareEnabled}
              isLoading={isLoading}
              className={className}
              style={sticky.style}
            />
          );
          if (!placementInserted && beforeFirstMetric) {
            placementInserted = true;
            return (
              <Fragment key={`wrap-${id}`}>
                {beforeFirstMetric}
                {cell}
              </Fragment>
            );
          }
          return cell;
        }
        return (
          <Fragment key={id}>
            {renderEntityCell(id, { columnIndex: index, className, style: sticky.style, isDragged })}
          </Fragment>
        );
      })}
      {!placementInserted && beforeFirstMetric ? beforeFirstMetric : null}
    </>
  );
}

/** Totals footer aligned to an interleaved entity+metric column order. */
export function WorkspaceOrderedTotalsRow({
  order,
  current,
  previous,
  compareEnabled,
  isLoading,
  metricColumns = WORKSPACE_METRIC_COLUMNS,
  frozenColumnIds,
  frozenLeftById = {},
  selectionCol = true,
  trailingColSpan = 0,
  beforeFirstMetricColSpan = 0,
}: {
  order: string[];
  current: Record<string, number> | null;
  previous: Record<string, number> | null;
  compareEnabled: boolean;
  isLoading?: boolean;
  metricColumns?: WorkspaceMetricColumnDef[];
  frozenColumnIds?: readonly string[];
  frozenLeftById?: Record<string, number>;
  selectionCol?: boolean;
  trailingColSpan?: number;
  beforeFirstMetricColSpan?: number;
}) {
  const metricByKey = metricColumnsByKey(metricColumns);
  const derivedCurrent = {
    ...(current ?? {}),
    ...ensureDerivedMetrics(current),
  } as Record<string, number>;
  const derivedPrevious = {
    ...(previous ?? {}),
    ...ensureDerivedMetrics(previous),
  } as Record<string, number>;
  let placementInserted = false;
  let labelPlaced = false;

  return (
    <tr className="border-t-2 border-border bg-surface-muted/60">
      {selectionCol ? (
        <td className={workspaceStickyCheckboxFooterClass()} />
      ) : null}
      {order.map((id) => {
        const metric = metricByKey.get(id);
        const sticky = workspaceColumnStickyProps(id, frozenColumnIds, frozenLeftById, 'footer');
        if (metric) {
          const cells = (
            <WorkspaceMetricCell
              key={id}
              def={metric}
              current={derivedCurrent[id] ?? null}
              previous={derivedPrevious[id] ?? null}
              compareEnabled={compareEnabled}
              isLoading={isLoading}
              className={sticky.className}
              style={sticky.style}
            />
          );
          if (!placementInserted && beforeFirstMetricColSpan > 0) {
            placementInserted = true;
            return (
              <Fragment key={`tot-${id}`}>
                <td
                  className={`${workspaceTableClass.td} ${workspaceStickyFooterFillClass()}`}
                  colSpan={beforeFirstMetricColSpan}
                />
                {cells}
              </Fragment>
            );
          }
          return cells;
        }
        if (!labelPlaced) {
          labelPlaced = true;
          return (
            <td
              key={id}
              className={`${workspaceTableClass.td} ${sticky.className} text-left text-[11px] font-bold text-foreground`}
              style={sticky.style}
            >
              Total
            </td>
          );
        }
        return (
          <td key={id} className={`${workspaceTableClass.td} ${workspaceStickyFooterFillClass()}`} />
        );
      })}
      {!placementInserted && beforeFirstMetricColSpan > 0 ? (
        <td
          className={`${workspaceTableClass.td} ${workspaceStickyFooterFillClass()}`}
          colSpan={beforeFirstMetricColSpan}
        />
      ) : null}
      {trailingColSpan > 0 ? (
        <td
          className={`${workspaceTableClass.td} ${workspaceStickyFooterFillClass()}`}
          colSpan={trailingColSpan}
        />
      ) : null}
    </tr>
  );
}
